/**
 * The upsell dialog, opened from a 402 ENTITLEMENT_MISSING problem (any query or mutation, via
 * the QueryClient error hooks) or from a locked nav item / locked button. It renders the payload
 * the API sent (`feature`, `plan_hint`, `upgrade_url`): no second call (docs/03 "Error contract").
 */
import type { Problem } from '@oex/contracts';
import { UpsellDialog, type UpsellPayload } from '@oex/ui';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useT } from './i18n';

type UpsellApi = { open(payload: UpsellPayload): void; openFromProblem(problem: Problem): void };
const UpsellContext = createContext<UpsellApi | null>(null);

export function payloadFromProblem(problem: Problem): UpsellPayload {
  return {
    feature: problem.feature ?? 'unknown',
    ...(problem.reason ? { reason: problem.reason } : {}),
    planHint: problem.plan_hint ?? null,
    upgradeUrl: problem.upgrade_url ?? null,
  };
}

/** Upsell host; `bridge` lets the QueryClient (created outside React) open it. */
export function UpsellProvider({
  children,
  bridge,
}: {
  children: ReactNode;
  bridge?: { current: UpsellApi | null };
}) {
  const t = useT();
  const [payload, setPayload] = useState<UpsellPayload | null>(null);
  const open = useCallback((p: UpsellPayload) => setPayload(p), []);
  const openFromProblem = useCallback((problem: Problem) => setPayload(payloadFromProblem(problem)), []);
  const api = useMemo(() => ({ open, openFromProblem }), [open, openFromProblem]);
  if (bridge) bridge.current = api;
  const featureLabel = payload ? t(`app.feature.${payload.feature}`) : '';
  return (
    <UpsellContext.Provider value={api}>
      {children}
      <UpsellDialog
        open={payload !== null}
        onOpenChange={(o) => {
          if (!o) setPayload(null);
        }}
        payload={payload}
        labels={{
          title: t('app.upsell.title'),
          body: t('app.upsell.body', {
            feature: featureLabel.startsWith('app.feature.') ? (payload?.feature ?? '') : featureLabel,
          }),
          plan: payload?.planHint ? t('app.upsell.plan', { plan: payload.planHint.name }) : null,
          upgrade: t('app.upsell.upgrade'),
          close: t('app.close'),
        }}
      />
    </UpsellContext.Provider>
  );
}

export function useUpsell(): UpsellApi {
  const api = useContext(UpsellContext);
  if (!api) throw new Error('useUpsell outside <UpsellProvider>');
  return api;
}
